import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, X, Clock, Plus } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES } from "../data/blogPosts";
import { optimizedSrc } from "../lib/img";
import { applyPostSeo, resetSeo, slugFromPath } from "../lib/seo";

const findPost = (slug) => BLOG_POSTS.find((p) => p.id === slug) || null;

const INITIAL_VISIBLE = 6;
const LOAD_STEP = 6;

export default function Blog({ sectionHidden = false, onDeepLinkClose } = {}) {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(() =>
    typeof window !== "undefined" ? findPost(slugFromPath(window.location.pathname)) : null
  );
  const [visible, setVisible] = useState(INITIAL_VISIBLE);
  const pushedRef = useRef(false);

  // Each article has its own crawlable URL: /blog/<slug>
  const openPost = useCallback((post) => {
    setActive(post);
    if (slugFromPath(window.location.pathname) !== post.id) {
      window.history.pushState({ post: post.id }, "", `/blog/${post.id}`);
      pushedRef.current = true;
    }
  }, []);

  const closePost = useCallback(() => {
    setActive(null);
    if (slugFromPath(window.location.pathname)) {
      if (pushedRef.current) {
        pushedRef.current = false;
        window.history.back();
      } else {
        // Arrived directly on /blog/<slug>: reveal the full page, land on the blog
        window.history.replaceState({}, "", "/#blog");
        if (onDeepLinkClose) onDeepLinkClose();
        setTimeout(() => document.getElementById("blog")?.scrollIntoView(), 60);
      }
    }
  }, [onDeepLinkClose]);

  // Back/forward buttons open/close the matching article
  useEffect(() => {
    const onPop = () => {
      pushedRef.current = false;
      setActive(findPost(slugFromPath(window.location.pathname)));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);


  // Per-article title, description, canonical and social tags
  useEffect(() => {
    if (active) applyPostSeo(active);
    else resetSeo();
  }, [active]);

  // Close with Escape key
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === "Escape" && closePost();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, closePost]);

  const filtered = useMemo(
    () =>
      filter === "All"
        ? BLOG_POSTS
        : BLOG_POSTS.filter((p) =>
            (p.categories || [p.category]).includes(filter)
          ),
    [filter]
  );

  // Reset visible count when filter changes
  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [filter]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <>
    <section
      id="blog"
      data-testid="blog-section"
      className="relative py-28 bg-white overflow-hidden"
      hidden={sectionHidden}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl">
          <p className="pill mb-5 reveal">The Joint Health Blog</p>
          <h2
            className="h-display text-3xl sm:text-4xl title-underline reveal"
            data-testid="blog-title"
          >
            Dr. Maggie's Joint Health Blog
          </h2>
          <p className="mt-7 text-charcoal/80 reveal">
            Patient-friendly education from Dr. Maggie on arthritis, lupus, autoimmune disease,
            movement, nutrition, women's health, minority health, and living well. {BLOG_POSTS.length}{" "}
            articles and growing.
          </p>
        </div>

        {/* Category filter */}
        <div
          data-testid="blog-filters"
          className="mt-10 flex flex-wrap gap-2.5"
          role="tablist"
          aria-label="Filter blog posts by category"
        >
          {BLOG_CATEGORIES.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={filter === c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`pill-filter ${filter === c ? "active" : ""}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-grid">
          {shown.map((post, i) => (
            <article
              key={post.id}
              id={post.id}
              data-testid={`blog-card-${post.id}`}
              className="card-soft overflow-hidden group flex flex-col scroll-mt-24"
              style={{ animation: `fadeUp .8s ${i * 80}ms both` }}
            >
              <div className="rounded-none relative bg-gradient-to-br from-lavender-bg via-white to-pale-blue/60 overflow-hidden">
                <img
                  src={optimizedSrc(post.image, 800)}
                  srcSet={
                    post.image.startsWith("http")
                      ? `${optimizedSrc(post.image, 480)} 480w, ${optimizedSrc(post.image, 800)} 800w`
                      : undefined
                  }
                  sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw"
                  alt={post.alt}
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="208"
                  className="w-full h-52 object-contain transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="pill" data-testid={`blog-card-cat-${post.id}`}>
                    {post.category}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-charcoal/75">
                    <Clock size={11} /> {post.read}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-[15px] tracking-[0.16em] uppercase leading-snug text-plum">
                  {post.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-charcoal/75 flex-1">
                  {post.excerpt}
                </p>
                <a
                  href={`/blog/${post.id}`}
                  data-testid={`blog-read-more-${post.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    openPost(post);
                  }}
                  className="mt-5 self-start inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase text-white bg-gradient-to-r from-plum to-violet hover:from-violet hover:to-plum transition-all duration-500 shadow-[0_8px_22px_-10px_rgba(111,45,168,0.55)]"
                >
                  Read More<span className="sr-only">: {post.title}</span>{" "}
                  <ArrowRight size={13} aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-10 text-center text-charcoal/60">
            No posts in this category yet. Check back soon.
          </p>
        )}

        {hasMore && (
          <div className="mt-14 flex justify-center">
            <button
              data-testid="blog-load-more"
              onClick={() => setVisible((v) => v + LOAD_STEP)}
              className="btn-outline-purple group"
            >
              <Plus size={16} className="transition-transform group-hover:rotate-90 duration-500" />
              Show More Articles
              <span className="text-[11px] opacity-90">
                ({filtered.length - visible} more)
              </span>
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>

      {/* Read More Modal */}
      {active && (
        <div
          data-testid="blog-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-plum/40 backdrop-blur-sm px-4 py-6 animate-fade-in"
          onClick={closePost}
          role="dialog"
          aria-modal="true"
          aria-labelledby="blog-modal-title"
        >
          <div
            className="relative bg-white max-w-2xl w-full rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="blog-modal-close"
              onClick={closePost}
              autoFocus
              className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/90 backdrop-blur grid place-items-center text-plum hover:bg-royal hover:text-white transition-colors"
              aria-label="Close article"
            >
              <X size={16} />
            </button>
            <div className="w-full h-60 bg-gradient-to-br from-lavender-bg via-white to-pale-blue/60 flex items-center justify-center">
              <img
                src={optimizedSrc(active.image, 640)}
                alt={active.alt}
                fetchPriority="high"
                width="360"
                height="240"
                className="w-auto h-auto max-h-60 max-w-full object-contain"
              />
            </div>
            <div className="p-7 sm:p-9 overflow-y-auto">
              <span className="pill">{active.category}</span>
              <h2
                id="blog-modal-title"
                className="mt-4 font-display text-2xl sm:text-3xl tracking-[0.1em] uppercase text-plum leading-tight"
              >
                {active.title}
              </h2>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-charcoal/75">
                {active.read}
              </div>
              <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-charcoal/85">
                {active.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <p className="mt-8 text-[12px] italic text-charcoal/60 border-t border-lavender/50 pt-5">
                This article is for educational purposes only and does not replace medical advice
                from your physician.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
